/**
 * VerificationPage — the skill-matched Verification window (DEV-1379, Content
 * Verification Stage 3). The PULL surface: a reviewer finds work by expertise.
 * See SPEC_CONTENT_VERIFICATION.md §4 Stage 3, §8, §6.
 *
 * Approach-C (LOCKED): two page states.
 *   - Hub: "What you're qualified to verify" + lane cards. Two ACTIVE
 *     qualifications (DEV-1403, the Gate-1/Gate-2 split):
 *       • Community standards (Gate 1, `activeQual==='community'`): per-community
 *         norms/on-topic review, organized by the communities you belong to.
 *         Unlocks community feeds. Review → verdict directly (no claim step in v1).
 *       • Expert review (Gate 2, `activeQual==='content'`): the EXISTING skill-
 *         matched content-review drill, relabelled. Unlocks search. Untouched
 *         skills rail + pool + claim + verdict Tray.
 *     Presence/Reality + Personhood remain locked. The Rail is empty in the hub —
 *     one navigator, lanes are not duplicated into the Rail.
 *   - Lane (Expert review): Rail (reused, widthVariant 'list') of the user's
 *     skills as filters with a back-to-hub control pinned at the TOP; main area
 *     lists the pool matched via reviewMatch (a CLONE of resourceMatch); Tray
 *     (reused) opens the selected item with verdict controls.
 *   - Lane (Community standards): Rail of the distinct communities derived from
 *     the community pool (no separate fetch) as filters; main lists the pool;
 *     Tray opens the selected item with NORMS-framed dispositions.
 *
 * Minor gating (SPEC §6/§9#4): if the pool endpoint 403s (under-18), the window
 * renders a friendly "not available" state — hidden for minors by default.
 *
 * Composes with POLICY_NAVIGATION_RAILS.md: at most two left columns (LeftNav +
 * this page's Rail). The app-wide Communities/Resources rails self-gate off
 * '/verification', so there is no third nav layer.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { getCurrentUserGuid, ApiError } from '../../api/client';
import { fetchUserSkills, type UserSkill } from '../../api/userSkills';
import {
  fetchVerificationPool,
  claimReviewItem,
  returnReviewItem,
  reviewChronicle,
  fetchCommunityReviewPool,
  reviewCommunityStandards,
  type VerificationPoolItem,
  type CommunityPoolItem,
} from '../../api/verification';
import { fetchChronicle, type ChronicleResponse } from '../../api/chronicles';
import Rail from '../../Components/Rail/Rail';
import { Tray } from '../../Components/Tray/Tray';
import {
  scoreSkillsAgainstItem,
  type ReviewMatchBucket,
} from '../../Data/Verification/reviewMatch';
import styles from './VerificationPage.module.css';

const MAX_CLAIMS = 10;

const ALL_SKILLS = '__all__';
const ALL_COMMUNITIES = '__all__';

export default function VerificationPage() {
  const { t } = useAppTranslation('pages');
  const me = getCurrentUserGuid();

  // Two-level drill: level 1 = qualifications (verification types), level 2 =
  // the skills within the chosen qualification. The Rail is ALWAYS present and
  // slides between the two — it never disappears (POLICY_NAVIGATION_RAILS).
  // (DEV-1379, Skip 2026-06-24.)
  const [level, setLevel] = useState<1 | 2>(1);
  const [activeQual, setActiveQual] = useState<string | null>(null);

  // ── Skills (async hook data) ──
  const [skills, setSkills] = useState<UserSkill[]>([]);
  const [skillsLoaded, setSkillsLoaded] = useState(false);

  // ── Pool (async hook data) ──
  const [pool, setPool] = useState<VerificationPoolItem[]>([]);
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolError, setPoolError] = useState<string | null>(null);
  const [minorBlocked, setMinorBlocked] = useState(false);

  // ── Rail filter + Tray selection ──
  const [selectedSkill, setSelectedSkill] = useState<string>(ALL_SKILLS);
  const [openItem, setOpenItem] = useState<VerificationPoolItem | null>(null);

  // ── Community-standards pool (Gate 1, async hook data) ──
  const [communityPool, setCommunityPool] = useState<CommunityPoolItem[]>([]);
  const [communityPoolLoading, setCommunityPoolLoading] = useState(false);
  const [communityPoolError, setCommunityPoolError] = useState<string | null>(null);
  const [selectedCommunity, setSelectedCommunity] = useState<string>(ALL_COMMUNITIES);
  const [openCommunityItem, setOpenCommunityItem] = useState<CommunityPoolItem | null>(null);

  // ── Active-claim count (for the cap counter) ──
  const activeClaimCount = useMemo(
    () => pool.filter((p) => p.claimedByMe).length,
    [pool],
  );

  // Load skills once (drives the standing line + Rail filters + match labels).
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    fetchUserSkills(me)
      .then((data) => { if (!cancelled) setSkills(data); })
      .catch(() => { if (!cancelled) setSkills([]); })
      .finally(() => { if (!cancelled) setSkillsLoaded(true); });
    return () => { cancelled = true; };
  }, [me]);

  // Load the pool when entering the content lane.
  const loadPool = useCallback(() => {
    setPoolLoading(true);
    setPoolError(null);
    setMinorBlocked(false);
    fetchVerificationPool()
      .then((data) => setPool(data))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 403) {
          setMinorBlocked(true);
        } else {
          setPoolError(err instanceof Error ? err.message : t('verification.lane.error'));
        }
      })
      .finally(() => setPoolLoading(false));
  }, [t]);

  useEffect(() => {
    if (level === 2 && activeQual === 'content') loadPool();
  }, [level, activeQual, loadPool]);

  // ── Community-standards pool (Gate 1) ──
  const loadCommunityPool = useCallback(() => {
    setCommunityPoolLoading(true);
    setCommunityPoolError(null);
    setMinorBlocked(false);
    fetchCommunityReviewPool()
      .then((data) => setCommunityPool(data))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 403) {
          setMinorBlocked(true);
        } else {
          setCommunityPoolError(err instanceof Error ? err.message : t('verification.community.error'));
        }
      })
      .finally(() => setCommunityPoolLoading(false));
  }, [t]);

  useEffect(() => {
    if (level === 2 && activeQual === 'community') loadCommunityPool();
  }, [level, activeQual, loadCommunityPool]);

  // Distinct communities derived from the pool (no separate fetch). communityPool
  // is async-hook-derived → MUST be in the deps of the memo below.
  const communityFilters = useMemo(() => {
    const map = new Map<string, { communityGuid: string; communityName: string; count: number }>();
    for (const item of communityPool) {
      const key = item.communityGuid.toUpperCase();
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(key, {
          communityGuid: item.communityGuid,
          communityName: item.communityName || item.communityGuid,
          count: 1,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.communityName.localeCompare(b.communityName),
    );
  }, [communityPool]);

  // Pool filtered by the selected community filter. communityPool +
  // selectedCommunity are async/state-derived → in deps.
  const filteredCommunityPool = useMemo(() => {
    if (selectedCommunity === ALL_COMMUNITIES) return communityPool;
    return communityPool.filter(
      (item) => item.communityGuid.toUpperCase() === selectedCommunity.toUpperCase(),
    );
  }, [communityPool, selectedCommunity]);

  // ── Match scoring: reviewer skill labels × each item's matchText ──
  // skills is async-hook-derived → MUST be in the deps of the memo below.
  const skillLabels = useMemo(() => skills.map((s) => s.label), [skills]);

  const rankedPool = useMemo(() => {
    // Filter labels by the selected skill (or all).
    const labelsForMatch =
      selectedSkill === ALL_SKILLS
        ? skillLabels
        : skills.filter((s) => s.slug === selectedSkill).map((s) => s.label);

    const scored = pool.map((item) => ({
      item,
      score: scoreSkillsAgainstItem(labelsForMatch, item.matchText),
    }));

    // When a specific skill is selected, keep only items that share a keyword
    // with it (band !== 'low' OR has a shared keyword). When 'all', keep all.
    const filtered =
      selectedSkill === ALL_SKILLS
        ? scored
        : scored.filter((s) => s.score.sharedKeywords.length > 0);

    return filtered.sort((a, b) => b.score.total - a.score.total);
  }, [pool, skills, skillLabels, selectedSkill]);

  // ── Claim / return ──
  const [claimBusy, setClaimBusy] = useState<string | null>(null);

  const handleClaim = useCallback(async (item: VerificationPoolItem) => {
    if (claimBusy) return;
    setClaimBusy(item.chronicleGuid);
    try {
      await claimReviewItem(item.chronicleGuid);
      loadPool();
    } catch (err: unknown) {
      setPoolError(err instanceof Error ? err.message : t('verification.card.claimError'));
    } finally {
      setClaimBusy(null);
    }
  }, [claimBusy, loadPool, t]);

  const handleReturn = useCallback(async (item: VerificationPoolItem) => {
    if (claimBusy) return;
    setClaimBusy(item.chronicleGuid);
    try {
      await returnReviewItem(item.chronicleGuid);
      setOpenItem(null);
      loadPool();
    } catch (err: unknown) {
      setPoolError(err instanceof Error ? err.message : t('verification.card.claimError'));
    } finally {
      setClaimBusy(null);
    }
  }, [claimBusy, loadPool, t]);

  // ════════════════════════════════════════════════════════════════
  //  Two-level drill — Qualifications (level 1) → Skills (level 2).
  //  "Qualification" = a TYPE of thing you can verify (Content review,
  //  Presence/Reality, Personhood). "Skill" = a competency within Content
  //  review. The Rail is always present; its content slides between levels.
  // ════════════════════════════════════════════════════════════════

  const enterQualification = (key: string) => {
    setActiveQual(key);
    setSelectedSkill(ALL_SKILLS);
    setSelectedCommunity(ALL_COMMUNITIES);
    setLevel(2);
  };
  const backToQualifications = () => {
    setLevel(1);
    setActiveQual(null);
    setOpenItem(null);
    setOpenCommunityItem(null);
  };

  // Gate-1 disposition → backend verdict mapping (NORMS-framed labels):
  // Looks good → approved, Needs work → changes_requested, Off-topic → rejected.
  const handleCommunityReviewed = useCallback(() => {
    setOpenCommunityItem(null);
    loadCommunityPool();
  }, [loadCommunityPool]);

  const skillCount = skills.length;

  return (
    <div className={styles.laneLayout}>
      <Rail
        ariaLabel={t('verification.rail.ariaLabel')}
        widthVariant="list"
        persistKey="l4e_rail_verification_collapsed"
        /* DEV-1407 — converged onto the primitive's ONE collapsed-label
           mechanism. This rail previously hand-rolled the same thing (a
           `.collapsedLabel` div plus a `[data-collapsed]` CSS rule); both are
           deleted. The label still keys off the current level. */
        collapsedLabel={
          level === 1
            ? t('verification.rail.qualificationsHeader')
            : activeQual === 'community'
              ? t('verification.community.collapsed')
              : t('verification.rail.collapsedSkills')
        }
      >
        <div className={styles.railViewport}>
          <div
            className={styles.railTrack}
            style={{ transform: level === 2 ? 'translateX(-50%)' : 'translateX(0)' }}
          >
            {/* Level 1 — Qualifications (the verification types) */}
            <div className={styles.railPanel}>
              <div className={styles.railHeader}>{t('verification.rail.qualificationsHeader')}</div>
              <ul className={styles.skillFilterList}>
                <li>
                  <button
                    type="button"
                    className={styles.qualRow}
                    onClick={() => enterQualification('community')}
                  >
                    <span className={styles.skillFilterLabel}>{t('verification.hub.laneCommunityTitle')}</span>
                    <ChevronRight size={15} aria-hidden="true" />
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className={styles.qualRow}
                    onClick={() => enterQualification('content')}
                  >
                    <span className={styles.skillFilterLabel}>{t('verification.hub.laneExpertTitle')}</span>
                    <ChevronRight size={15} aria-hidden="true" />
                  </button>
                </li>
                <li>
                  <div className={`${styles.qualRow} ${styles.qualRowLocked}`} aria-disabled="true">
                    <span className={styles.skillFilterLabel}>{t('verification.hub.lanePresenceTitle')}</span>
                    <Lock size={13} aria-hidden="true" />
                  </div>
                </li>
                <li>
                  <div className={`${styles.qualRow} ${styles.qualRowLocked}`} aria-disabled="true">
                    <span className={styles.skillFilterLabel}>{t('verification.hub.lanePersonhoodTitle')}</span>
                    <Lock size={13} aria-hidden="true" />
                  </div>
                </li>
              </ul>
            </div>

            {/* Level 2 — filters within the active qualification.
                Expert review → skills; Community standards → communities. */}
            <div className={styles.railPanel}>
              <button type="button" className={styles.backToHub} onClick={backToQualifications}>
                <ChevronLeft size={16} aria-hidden="true" />
                <span>{t('verification.rail.backToQualifications')}</span>
              </button>
              {activeQual === 'community' ? (
                <>
                  <div className={styles.railHeader}>{t('verification.community.railHeader')}</div>
                  <ul className={styles.skillFilterList}>
                    <li>
                      <button
                        type="button"
                        className={`${styles.skillFilter} ${selectedCommunity === ALL_COMMUNITIES ? styles.skillFilterActive : ''}`}
                        onClick={() => setSelectedCommunity(ALL_COMMUNITIES)}
                      >
                        {t('verification.community.allCommunities')}
                      </button>
                    </li>
                    {communityFilters.map((c) => (
                      <li key={c.communityGuid}>
                        <button
                          type="button"
                          className={`${styles.skillFilter} ${selectedCommunity === c.communityGuid ? styles.skillFilterActive : ''}`}
                          onClick={() => setSelectedCommunity(c.communityGuid)}
                        >
                          <span className={styles.skillFilterLabel}>{c.communityName}</span>
                          <span className={styles.filterCount}>{c.count}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <div className={styles.railHeader}>{t('verification.hub.laneExpertTitle')}</div>
                  <ul className={styles.skillFilterList}>
                    <li>
                      <button
                        type="button"
                        className={`${styles.skillFilter} ${selectedSkill === ALL_SKILLS ? styles.skillFilterActive : ''}`}
                        onClick={() => setSelectedSkill(ALL_SKILLS)}
                      >
                        {t('verification.rail.allSkills')}
                      </button>
                    </li>
                    {skills.map((s) => (
                      <li key={s.userSkillGuid}>
                        <button
                          type="button"
                          className={`${styles.skillFilter} ${selectedSkill === s.slug ? styles.skillFilterActive : ''}`}
                          onClick={() => setSelectedSkill(s.slug)}
                        >
                          <span className={styles.skillFilterLabel}>{s.label}</span>
                          <LadderBadge skill={s} t={t} />
                        </button>
                      </li>
                    ))}
                    {skillsLoaded && skills.length === 0 && (
                      <li className={styles.railEmpty}>{t('verification.rail.noSkills')}</li>
                    )}
                  </ul>
                  <Link
                    to="/profile#skills"
                    className={styles.skillsLink}
                    style={{ display: 'block', marginBlockStart: 12, paddingInline: 8, fontSize: 13 }}
                  >
                    {t('verification.hub.skillsLink')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </Rail>

      {/* Main */}
      <div className={styles.laneMain}>
        {level === 1 ? (
          <>
            <h1 className={styles.heading}>{t('verification.hub.heading')}</h1>
            <p className={styles.standingLine}>
              {skillCount > 0
                ? t('verification.hub.skillsLine', { count: skillCount })
                : t('verification.hub.skillsLineZero')}
              {' '}
              <Link to="/profile#skills" className={styles.skillsLink}>
                {t('verification.hub.skillsLink')}
              </Link>
            </p>
            <div className={styles.laneGrid}>
              <button type="button" className={styles.laneCard} onClick={() => enterQualification('community')}>
                <span className={styles.laneTitle}>{t('verification.hub.laneCommunityTitle')}</span>
                <span className={styles.laneDesc}>{t('verification.hub.laneCommunityDesc')}</span>
                <span className={styles.laneEnter}>{t('verification.hub.enter')} ›</span>
              </button>
              <button type="button" className={styles.laneCard} onClick={() => enterQualification('content')}>
                <span className={styles.laneTitle}>{t('verification.hub.laneExpertTitle')}</span>
                <span className={styles.laneDesc}>{t('verification.hub.laneExpertDesc')}</span>
                <span className={styles.laneEnter}>{t('verification.hub.enter')} ›</span>
              </button>
              <div className={`${styles.laneCard} ${styles.laneLocked}`} aria-disabled="true">
                <span className={styles.laneTitle}>{t('verification.hub.lanePresenceTitle')}</span>
                <span className={styles.laneDesc}>{t('verification.hub.lanePresenceDesc')}</span>
                <span className={styles.laneBadge}>{t('verification.hub.comingSoon')}</span>
              </div>
              <div className={`${styles.laneCard} ${styles.laneLocked}`} aria-disabled="true">
                <span className={styles.laneTitle}>{t('verification.hub.lanePersonhoodTitle')}</span>
                <span className={styles.laneDesc}>{t('verification.hub.lanePersonhoodDesc')}</span>
                <span className={styles.laneBadge}>{t('verification.hub.comingSoon')}</span>
              </div>
            </div>
          </>
        ) : minorBlocked ? (
          <div className={styles.minorState}>
            <h2 className={styles.minorHeading}>{t('verification.minor.heading')}</h2>
            <p className={styles.minorBody}>{t('verification.minor.body')}</p>
          </div>
        ) : activeQual === 'community' ? (
          <>
            <h1 className={styles.heading}>{t('verification.community.heading')}</h1>
            <p className={styles.framingLine}>{t('verification.community.framing')}</p>

            {communityPoolError && <div className={styles.error}>{communityPoolError}</div>}

            {communityPoolLoading ? (
              <div className={styles.muted}>{t('verification.community.loading')}</div>
            ) : filteredCommunityPool.length === 0 ? (
              <div className={styles.muted}>
                {selectedCommunity === ALL_COMMUNITIES
                  ? t('verification.community.empty')
                  : t('verification.community.emptyFiltered')}
              </div>
            ) : (
              <ul className={styles.poolList}>
                {filteredCommunityPool.map((item) => (
                  <li key={`${item.chronicleGuid}-${item.communityGuid}`} className={styles.poolCard}>
                    <div className={styles.poolCardTop}>
                      <span className={styles.poolTitle}>{item.title}</span>
                    </div>
                    <div className={styles.poolMeta}>
                      <span className={styles.poolProgress}>
                        {item.communityName || item.communityGuid}
                      </span>
                    </div>
                    <div className={styles.poolActions}>
                      <button
                        type="button"
                        className={styles.openBtn}
                        onClick={() => setOpenCommunityItem(item)}
                      >
                        {t('verification.community.open')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <h1 className={styles.heading}>{t('verification.hub.laneExpertTitle')}</h1>
            <div className={styles.claimsCounter}>
              {t('verification.card.claimsCounter', { active: activeClaimCount, max: MAX_CLAIMS })}
            </div>

            {poolError && <div className={styles.error}>{poolError}</div>}

            {poolLoading ? (
              <div className={styles.muted}>{t('verification.lane.loading')}</div>
            ) : rankedPool.length === 0 ? (
              <div className={styles.muted}>
                {selectedSkill === ALL_SKILLS
                  ? t('verification.lane.empty')
                  : t('verification.lane.emptyFiltered')}
              </div>
            ) : (
              <ul className={styles.poolList}>
                {rankedPool.map(({ item, score }) => {
                  const capReached = !item.claimedByMe && activeClaimCount >= MAX_CLAIMS;
                  return (
                    <li key={item.chronicleGuid} className={styles.poolCard}>
                      <div className={styles.poolCardTop}>
                        <span className={styles.poolTitle}>{item.title}</span>
                        <MatchBadge bucket={score.bucket} t={t} />
                      </div>
                      <div className={styles.poolMeta}>
                        <span className={styles.poolCategory}>{item.categoryKey}</span>
                        <span className={styles.poolModel}>{item.reviewModel}</span>
                        <span className={styles.poolProgress}>
                          {t('verification.card.progress', {
                            approved: item.currentApprovedCount,
                            required: item.requiredApprovals,
                          })}
                        </span>
                      </div>
                      {score.sharedKeywords.length > 0 && (
                        <div className={styles.shared}>
                          <span className={styles.sharedLabel}>{t('verification.match.sharedLabel')}</span>
                          {score.sharedKeywords.map((k) => (
                            <span key={k} className={styles.sharedChip}>{k}</span>
                          ))}
                        </div>
                      )}
                      <div className={styles.poolActions}>
                        {item.claimedByMe ? (
                          <>
                            <span className={styles.claimedTag}>{t('verification.card.claimed')}</span>
                            <button
                              type="button"
                              className={styles.openBtn}
                              onClick={() => setOpenItem(item)}
                            >
                              {t('verification.card.open')}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className={styles.claimBtn}
                            disabled={capReached || claimBusy === item.chronicleGuid}
                            title={capReached ? t('verification.card.capReached') : undefined}
                            onClick={() => handleClaim(item)}
                          >
                            {t('verification.card.claim')}
                          </button>
                        )}
                        {item.activeClaimCount > 0 && (
                          <span className={styles.otherClaims}>
                            {t('verification.card.otherClaims', { count: item.activeClaimCount })}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      {/* Tray — the item under review */}
      <ReviewTray
        item={openItem}
        onClose={() => setOpenItem(null)}
        onReturn={handleReturn}
        onReviewed={() => { setOpenItem(null); loadPool(); }}
        t={t}
      />

      {/* Gate-1 Tray — community-standards review (no claim step in v1). */}
      <CommunityReviewTray
        item={openCommunityItem}
        onClose={() => setOpenCommunityItem(null)}
        onReviewed={handleCommunityReviewed}
        t={t}
      />
    </div>
  );
}

/* ================================================================== */
/*  Sub-components                                                      */
/* ================================================================== */

function LadderBadge({ skill, t }: { skill: UserSkill; t: (k: string) => string }) {
  let label: string;
  let cls: string;
  if (skill.isSme) {
    label = t('verification.badge.sme');
    cls = styles.badgeSme;
  } else if (skill.totalEndorsements > 0) {
    label = t('verification.badge.reviewAuthority');
    cls = styles.badgeAuthority;
  } else {
    label = t('verification.badge.discoveryOnly');
    cls = styles.badgeDiscovery;
  }
  return <span className={`${styles.ladderBadge} ${cls}`}>{label}</span>;
}

function MatchBadge({ bucket, t }: { bucket: ReviewMatchBucket; t: (k: string) => string }) {
  const map: Record<ReviewMatchBucket, { label: string; cls: string }> = {
    strong: { label: t('verification.match.strong'), cls: styles.matchStrong },
    partial: { label: t('verification.match.partial'), cls: styles.matchPartial },
    low: { label: t('verification.match.low'), cls: styles.matchLow },
  };
  const { label, cls } = map[bucket];
  return <span className={`${styles.matchBadge} ${cls}`}>{label}</span>;
}

interface ReviewTrayProps {
  item: VerificationPoolItem | null;
  onClose: () => void;
  onReturn: (item: VerificationPoolItem) => void;
  onReviewed: () => void;
  t: ReturnType<typeof useAppTranslation>['t'];
}

function ReviewTray({ item, onClose, onReturn, onReviewed, t }: ReviewTrayProps) {
  const [detail, setDetail] = useState<ChronicleResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [verdictBusy, setVerdictBusy] = useState(false);
  const [verdictError, setVerdictError] = useState<string | null>(null);
  const lastGuid = useRef<string | null>(null);

  // Load the chronicle preview when the open item changes. `item` is the
  // async-derived selection → in deps.
  useEffect(() => {
    if (!item) { setDetail(null); return; }
    if (lastGuid.current === item.chronicleGuid && detail) return;
    lastGuid.current = item.chronicleGuid;
    setDetailLoading(true);
    setVerdictError(null);
    setNotes('');
    fetchChronicle(item.chronicleGuid)
      .then((d) => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [item, detail]);

  const recordVerdict = useCallback(async (verdict: 'approved' | 'changes_requested' | 'rejected') => {
    if (!item || verdictBusy) return;
    if ((verdict === 'changes_requested' || verdict === 'rejected') && !notes.trim()) {
      setVerdictError(t('verification.tray.notesPlaceholder'));
      return;
    }
    setVerdictBusy(true);
    setVerdictError(null);
    try {
      // DEV-1392: reviewModel is no longer sent from the client — the server
      // derives the required-approval bar from the chronicle's template_key.
      await reviewChronicle(item.chronicleGuid, {
        verdict,
        notes: notes.trim() || undefined,
      });
      onReviewed();
    } catch (err: unknown) {
      setVerdictError(err instanceof Error ? err.message : t('verification.tray.verdictError'));
    } finally {
      setVerdictBusy(false);
    }
  }, [item, verdictBusy, notes, onReviewed, t]);

  return (
    <Tray open={item != null} onClose={onClose} title={item?.title ?? t('verification.tray.title')}>
      <Tray.Body className={styles.trayBody}>
        {detailLoading ? (
          <div className={styles.muted}>{t('verification.tray.loading')}</div>
        ) : (
          <>
            <section className={styles.traySection}>
              <h3 className={styles.trayHeading}>{t('verification.tray.previewHeading')}</h3>
              <div className={styles.trayPreview}>
                {detail?.missionNarrative
                  ? detail.missionNarrative
                  : detail?.background || '—'}
              </div>
            </section>

            <section className={styles.traySection}>
              <h3 className={styles.trayHeading}>{t('verification.tray.progressHeading')}</h3>
              <div className={styles.trayProgress}>
                {item && Array.from({ length: Math.max(item.requiredApprovals, 1) }).map((_, i) => (
                  <span
                    key={i}
                    className={`${styles.progressDot} ${item && i < item.currentApprovedCount ? styles.progressDotFilled : ''}`}
                    aria-hidden="true"
                  />
                ))}
                <span className={styles.trayProgressText}>
                  {item && t('verification.card.progress', {
                    approved: item.currentApprovedCount,
                    required: item.requiredApprovals,
                  })}
                </span>
              </div>
            </section>

            <section className={styles.traySection}>
              <h3 className={styles.trayHeading}>{t('verification.tray.verdictHeading')}</h3>
              <label className={styles.srOnly} htmlFor="verification-notes">
                {t('verification.tray.notesLabel')}
              </label>
              <textarea
                id="verification-notes"
                className={styles.trayNotes}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('verification.tray.notesPlaceholder')}
                rows={4}
              />
              {verdictError && <div className={styles.error}>{verdictError}</div>}
              <div className={styles.verdictButtons}>
                <button
                  type="button"
                  className={styles.approveBtn}
                  disabled={verdictBusy}
                  onClick={() => recordVerdict('approved')}
                >
                  {t('verification.tray.approve')}
                </button>
                <button
                  type="button"
                  className={styles.changesBtn}
                  disabled={verdictBusy}
                  onClick={() => recordVerdict('changes_requested')}
                >
                  {t('verification.tray.requestChanges')}
                </button>
                <button
                  type="button"
                  className={styles.rejectBtn}
                  disabled={verdictBusy}
                  onClick={() => recordVerdict('rejected')}
                >
                  {t('verification.tray.reject')}
                </button>
              </div>
              <p className={styles.publishNote}>{t('verification.tray.publishNote')}</p>
            </section>
          </>
        )}
      </Tray.Body>
      <Tray.Footer className={styles.trayFooter}>
        {item && (
          <button
            type="button"
            className={styles.returnBtn}
            disabled={verdictBusy}
            onClick={() => onReturn(item)}
          >
            {t('verification.tray.return')}
          </button>
        )}
      </Tray.Footer>
    </Tray>
  );
}

/* ================================================================== */
/*  Community-standards Tray (Gate 1)                                   */
/* ================================================================== */

interface CommunityReviewTrayProps {
  item: CommunityPoolItem | null;
  onClose: () => void;
  onReviewed: () => void;
  t: ReturnType<typeof useAppTranslation>['t'];
}

/**
 * Gate-1 community-standards review Tray. NORMS-framed dispositions map to the
 * backend verdicts: Looks good → approved, Needs work → changes_requested,
 * Off-topic → rejected. No claim step in v1 — the pool already excludes own /
 * already-reviewed content, so the reviewer records a disposition directly.
 */
function CommunityReviewTray({ item, onClose, onReviewed, t }: CommunityReviewTrayProps) {
  const [detail, setDetail] = useState<ChronicleResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastGuid = useRef<string | null>(null);

  // Load the chronicle preview when the open item changes. `item` is the
  // async-derived selection → in deps.
  useEffect(() => {
    if (!item) { setDetail(null); return; }
    if (lastGuid.current === item.chronicleGuid && detail) return;
    lastGuid.current = item.chronicleGuid;
    setDetailLoading(true);
    setError(null);
    setNotes('');
    fetchChronicle(item.chronicleGuid)
      .then((d) => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [item, detail]);

  const recordDisposition = useCallback(
    async (verdict: 'approved' | 'changes_requested' | 'rejected') => {
      if (!item || busy) return;
      if ((verdict === 'changes_requested' || verdict === 'rejected') && !notes.trim()) {
        setError(t('verification.communityTray.notesRequired'));
        return;
      }
      setBusy(true);
      setError(null);
      try {
        await reviewCommunityStandards({
          contentGuid: item.chronicleGuid,
          communityGuid: item.communityGuid,
          verdict,
          notes: notes.trim() || undefined,
        });
        onReviewed();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t('verification.communityTray.error'));
      } finally {
        setBusy(false);
      }
    },
    [item, busy, notes, onReviewed, t],
  );

  return (
    <Tray
      open={item != null}
      onClose={onClose}
      title={item?.title ?? t('verification.communityTray.title')}
    >
      <Tray.Body className={styles.trayBody}>
        {detailLoading ? (
          <div className={styles.muted}>{t('verification.tray.loading')}</div>
        ) : (
          <>
            <section className={styles.traySection}>
              <h3 className={styles.trayHeading}>{t('verification.communityTray.contextHeading')}</h3>
              <div className={styles.trayPreview}>
                {item?.communityName || item?.communityGuid || '—'}
              </div>
            </section>

            <section className={styles.traySection}>
              <h3 className={styles.trayHeading}>{t('verification.communityTray.previewHeading')}</h3>
              <div className={styles.trayPreview}>
                {detail?.missionNarrative
                  ? detail.missionNarrative
                  : detail?.background || '—'}
              </div>
            </section>

            <section className={styles.traySection}>
              <h3 className={styles.trayHeading}>{t('verification.communityTray.dispositionHeading')}</h3>
              <label className={styles.srOnly} htmlFor="community-review-notes">
                {t('verification.communityTray.notesLabel')}
              </label>
              <textarea
                id="community-review-notes"
                className={styles.trayNotes}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('verification.communityTray.notesPlaceholder')}
                rows={4}
              />
              {error && <div className={styles.error}>{error}</div>}
              <div className={styles.verdictButtons}>
                <button
                  type="button"
                  className={styles.approveBtn}
                  disabled={busy}
                  onClick={() => recordDisposition('approved')}
                >
                  {t('verification.communityTray.looksGood')}
                </button>
                <button
                  type="button"
                  className={styles.changesBtn}
                  disabled={busy}
                  onClick={() => recordDisposition('changes_requested')}
                >
                  {t('verification.communityTray.needsWork')}
                </button>
                <button
                  type="button"
                  className={styles.rejectBtn}
                  disabled={busy}
                  onClick={() => recordDisposition('rejected')}
                >
                  {t('verification.communityTray.offTopic')}
                </button>
              </div>
              <p className={styles.publishNote}>{t('verification.communityTray.framing')}</p>
            </section>
          </>
        )}
      </Tray.Body>
    </Tray>
  );
}
